import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { uploadBlob } from "../lib/media";
import Navbar from "../components/Navbar";
import AuthMedia from "../components/AuthMedia";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollText, Upload, Loader2, X, Save, Scale, Calendar, Clock, Heart, Sparkles, PenLine, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const RELEASE_OPTIONS = [
  { value: "immediate", label: "Subito", icon: Sparkles },
  { value: "date", label: "In una data", icon: Calendar },
  { value: "event", label: "A un evento", icon: Clock },
  { value: "death", label: "Dopo la scomparsa", icon: Heart },
];

const Testamento = () => {
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ content: "", files: [], release_type: "death", release_date: "", event_description: "", signature: "", signed: false, signed_at: null, deposit_type: "holographic", notary_office: "", notary_repertory: "", deposited_at: "" });
  const [notary, setNotary] = useState({ name: "", email: "" });

  useEffect(() => {
    api.get("/testament").then((res) => {
      const t = res.data.testament;
      if (t) setForm({ content: t.content || "", files: t.files || [], release_type: t.release_type || "death", release_date: t.release_date || "", event_description: t.event_description || "", signature: t.signature || "", signed: !!t.signed, signed_at: t.signed_at || null, deposit_type: t.deposit_type || "holographic", notary_office: t.notary_office || "", notary_repertory: t.notary_repertory || "", deposited_at: t.deposited_at || "" });
      if (res.data.notary) setNotary({ name: res.data.notary.name || "", email: res.data.notary.email || "" });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const data = await uploadBlob(file, file.name);
        setForm((f) => ({ ...f, files: [...f.files, data] }));
      }
      toast.success("Documento caricato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel caricamento");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/testament", { ...form, release_date: form.release_date || null, signature: form.signature || null, signed: !!form.signed });
      await api.put("/testament/notary", { name: notary.name || null, email: notary.email || null });
      const t = res.data.testament;
      setForm((f) => ({ ...f, signed: !!t.signed, signed_at: t.signed_at || null, signature: t.signature || "" }));
      toast.success(t.signed ? "Testamento firmato e salvato" : "Testamento salvato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (<div className="min-h-screen"><Navbar /><p className="max-w-3xl mx-auto px-6 py-10 text-muted-foreground">Caricamento…</p></div>);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-10 fade-in">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#FBEDE7] flex items-center justify-center">
            <ScrollText className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="font-serif text-4xl tracking-tight">Testamento</h1>
            <p className="text-muted-foreground mt-1">Scrivi le tue volontà e firmale, oppure indica il notaio presso cui è depositato l'originale. Verranno resi disponibili secondo le tue istruzioni.</p>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-border p-8 space-y-8">
          <div>
            <Label className="mb-3 block">Tipo di testamento</Label>
            <div className="grid sm:grid-cols-2 gap-3">
              <button onClick={() => set("deposit_type", "holographic")} data-testid="deposit-holographic"
                className={`text-left p-4 rounded-2xl border transition-colors ${form.deposit_type === "holographic" ? "border-primary bg-[#FBEDE7]" : "border-border bg-white hover:border-primary/40"}`}>
                <div className="flex items-center gap-2 mb-1"><PenLine className={`w-4 h-4 ${form.deposit_type === "holographic" ? "text-primary" : "text-muted-foreground"}`} /><span className="font-medium text-sm">Olografo / libero</span></div>
                <p className="text-xs text-muted-foreground">Scrivi o carica il testamento qui e firmalo digitalmente.</p>
              </button>
              <button onClick={() => set("deposit_type", "notary")} data-testid="deposit-notary"
                className={`text-left p-4 rounded-2xl border transition-colors ${form.deposit_type === "notary" ? "border-primary bg-[#FBEDE7]" : "border-border bg-white hover:border-primary/40"}`}>
                <div className="flex items-center gap-2 mb-1"><Scale className={`w-4 h-4 ${form.deposit_type === "notary" ? "text-primary" : "text-muted-foreground"}`} /><span className="font-medium text-sm">Depositato dal notaio</span></div>
                <p className="text-xs text-muted-foreground">L'originale è custodito da un notaio: qui indichi solo i riferimenti.</p>
              </button>
            </div>
          </div>

          <div>
            <Label className="mb-2 block">{form.deposit_type === "notary" ? "Note per i tuoi cari (facoltative)" : "Le tue volontà"}</Label>
            <Textarea value={form.content} onChange={(e) => set("content", e.target.value)} rows={form.deposit_type === "notary" ? 4 : 8}
              placeholder={form.deposit_type === "notary" ? "Indicazioni utili: presso quale notaio si trova, come procedere…" : "Scrivi qui il tuo testamento o le tue disposizioni…"}
              className="bg-[#F4F1EA] border-border rounded-xl resize-none" data-testid="testament-content-input" />
          </div>

          {form.deposit_type === "holographic" && (
          <div>
            <Label className="mb-3 block">Documenti originali (foto, scansioni, PDF)</Label>
            <div className="space-y-2">
              {form.files.map((f) => (
                <div key={f.storage_path} className="flex items-center gap-3 bg-[#F4F1EA] rounded-xl p-3">
                  <div className="flex-1 min-w-0">
                    {f.kind === "image"
                      ? <AuthMedia item={f} className="max-h-40 rounded-lg object-contain" />
                      : <AuthMedia item={f} />}
                  </div>
                  <button onClick={() => set("files", form.files.filter((x) => x.storage_path !== f.storage_path))} className="p-1.5 rounded-full hover:bg-destructive hover:text-white transition-colors" data-testid="testament-file-remove">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="mt-3 inline-flex items-center gap-2 text-sm text-primary hover:underline" data-testid="testament-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Allega un documento
            </button>
            <input ref={fileRef} type="file" accept="image/*,application/pdf,.doc,.docx" multiple onChange={handleUpload} className="hidden" data-testid="testament-file-input" />
          </div>
          )}

          <div>
            <Label className="mb-3 block">Quando renderlo disponibile</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {RELEASE_OPTIONS.map((o) => {
                const active = form.release_type === o.value;
                return (
                  <button key={o.value} onClick={() => set("release_type", o.value)}
                    className={`flex items-center gap-2 p-3 rounded-xl border text-sm transition-colors ${active ? "border-primary bg-[#FBEDE7]" : "border-border bg-white hover:border-primary/40"}`}
                    data-testid={`testament-release-${o.value}`}>
                    <o.icon className={`w-4 h-4 ${active ? "text-primary" : "text-muted-foreground"}`} /> {o.label}
                  </button>
                );
              })}
            </div>
            {form.release_type === "date" && (
              <Input type="date" value={form.release_date} onChange={(e) => set("release_date", e.target.value)} className="mt-3 bg-[#F4F1EA] border-border rounded-xl h-11 max-w-xs" data-testid="testament-date-input" />
            )}
            {form.release_type === "event" && (
              <Input value={form.event_description} onChange={(e) => set("event_description", e.target.value)} placeholder="Descrizione evento" className="mt-3 bg-[#F4F1EA] border-border rounded-xl h-11" data-testid="testament-event-input" />
            )}
          </div>

          <div className="border-t border-border pt-6">
            <div className="flex items-center gap-2 mb-3">
              <Scale className="w-5 h-5 text-[#81B29A]" />
              <Label className="text-base">{form.deposit_type === "notary" ? "Notaio depositario" : "Notaio (facoltativo)"}</Label>
            </div>
            <p className="text-sm text-muted-foreground mb-3">{form.deposit_type === "notary"
              ? "Indica il notaio presso cui è depositato l'originale. Dopo la verifica ufficiale della scomparsa, i tuoi eredi riceveranno questi riferimenti per recuperarlo."
              : "Dopo la verifica ufficiale della scomparsa, invieremo automaticamente al notaio un link sicuro per consultare il testamento."}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input value={notary.name} onChange={(e) => setNotary({ ...notary, name: e.target.value })} placeholder="Nome del notaio" className="bg-[#F4F1EA] border-border rounded-xl h-11" data-testid="notary-name-input" />
              <Input type="email" value={notary.email} onChange={(e) => setNotary({ ...notary, email: e.target.value })} placeholder="Email del notaio" className="bg-[#F4F1EA] border-border rounded-xl h-11" data-testid="notary-email-input" />
            </div>
            {form.deposit_type === "notary" && (
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <Input value={form.notary_office} onChange={(e) => set("notary_office", e.target.value)} placeholder="Studio / città" className="bg-[#F4F1EA] border-border rounded-xl h-11" data-testid="notary-office-input" />
                <Input value={form.notary_repertory} onChange={(e) => set("notary_repertory", e.target.value)} placeholder="N. di repertorio / raccolta" className="bg-[#F4F1EA] border-border rounded-xl h-11" data-testid="notary-repertory-input" />
                <div>
                  <Label className="mb-1.5 block text-sm text-muted-foreground">Data del deposito (facoltativa)</Label>
                  <Input type="date" value={form.deposited_at} onChange={(e) => set("deposited_at", e.target.value)} className="bg-[#F4F1EA] border-border rounded-xl h-11 max-w-xs" data-testid="notary-deposited-input" />
                </div>
              </div>
            )}
          </div>

          {form.deposit_type === "holographic" && (
          <div className="border-t border-border pt-6">
            <div className="flex items-center gap-2 mb-3">
              <PenLine className="w-5 h-5 text-primary" />
              <Label className="text-base">Firma digitale</Label>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Firma il tuo testamento per renderlo formale. Verranno registrate la firma e la data, mostrate al notaio e ai tuoi eredi.</p>
            {form.signed && form.signed_at ? (
              <div className="flex items-start gap-3 bg-[#EAF3EE] border border-[#81B29A]/40 rounded-xl p-4" data-testid="testament-signed-badge">
                <CheckCircle2 className="w-5 h-5 text-[#4e8f6f] mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-[#2C2A29]">Firmato digitalmente da {form.signature}</p>
                  <p className="text-sm text-muted-foreground">il {new Date(form.signed_at).toLocaleString("it-IT", { dateStyle: "long", timeStyle: "short" })}</p>
                  <button onClick={() => set("signed", false)} className="text-sm text-primary hover:underline mt-1" data-testid="testament-unsign-btn">Modifica firma</button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Input value={form.signature} onChange={(e) => set("signature", e.target.value)} placeholder="Firma con il tuo nome completo" className="bg-[#F4F1EA] border-border rounded-xl h-11" data-testid="testament-signature-input" />
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input type="checkbox" checked={form.signed} onChange={(e) => set("signed", e.target.checked)} className="mt-1 w-4 h-4 accent-[#c9694f]" data-testid="testament-signed-checkbox" />
                  <span className="text-sm text-[#3d3a38]">Confermo che questo è il mio testamento e rappresenta le mie volontà.</span>
                </label>
              </div>
            )}
          </div>
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-[#c9694f] text-white h-11 px-8" data-testid="testament-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Salva testamento
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Testamento;
